import { forwardRef } from 'react';

import { cn } from '../utils';
import { skeletonRecipe, skeletonStackRecipe } from './Skeleton.recipe';

import type { SkeletonProps } from './types';

const MAX_LINES = 10;

/** Non-finite becomes 1, everything else is rounded and clamped to 1 to 10. */
const clampLines = (lines: number) =>
  Number.isFinite(lines) ? Math.min(MAX_LINES, Math.max(1, Math.round(lines))) : 1;

/**
 * Design System 2.0 skeleton: a placeholder for content whose shape is known before it arrives.
 *
 * When the shape is not known, use `Spinner`. A skeleton is exactly the size of what it stands in
 * for, so size it with `className` to match the real content.
 *
 * SILENT. `aria-hidden` is applied after the spread and left out of the prop type. A skeleton never
 * announces; the region that shows skeletons supplies one visually hidden `role="status"` naming what
 * is loading. Nothing in this component can do that on the region's behalf.
 */
export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(
  ({ shape = 'text', lines = 1, className, ...rest }, ref) => {
    const count = shape === 'text' ? clampLines(lines) : 1;

    if (count === 1) {
      return <span ref={ref} {...rest} aria-hidden className={cn(skeletonRecipe({ shape }), className)} />;
    }

    return (
      <span ref={ref} {...rest} aria-hidden className={cn(skeletonStackRecipe(), className)}>
        {Array.from({ length: count }, (_, index) => (
          <span key={index} className={cn(skeletonRecipe({ shape }), index === count - 1 && 'w-3/5')} />
        ))}
      </span>
    );
  }
);
